import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import Sidebar from './Sidebar'

/**
 * Operator app shell.
 *
 * Layout:
 *   - lg+  : sidebar sits statically to the left, always visible.
 *   - < lg : sidebar collapses to a slide-in drawer with a hamburger
 *            trigger in a mobile top-bar. Route change auto-closes.
 *
 * A11y:
 *   - Skip-to-content link (first-tab focusable, appears top-left).
 *   - <main id="main"> so the skip link can target it.
 *   - Sidebar drawer traps clicks on the backdrop.
 *   - Focus + body-scroll are managed in the drawer branch below.
 */
export default function AppLayout() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()

  // Route change closes the drawer.
  useEffect(() => { setMobileOpen(false) }, [location.pathname])

  // Lock body scroll while the drawer is open on mobile.
  useEffect(() => {
    if (!mobileOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e) => { if (e.key === 'Escape') setMobileOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      document.removeEventListener('keydown', onKey)
    }
  }, [mobileOpen])

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <a href="#main" className="skip-link">Skip to content</a>

      {/* Static sidebar (lg+) */}
      <div className="hidden lg:block">
        <Sidebar />
      </div>

      {/* Drawer (< lg) */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40" role="dialog" aria-modal="true" aria-label="Navigation">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          <div className="relative h-full w-72 max-w-[85vw] shadow-2xl">
            <Sidebar />
            <button
              className="absolute top-4 right-4 p-2 rounded-lg text-slate-300 hover:bg-white/10"
              onClick={() => setMobileOpen(false)}
              aria-label="Close navigation"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile top-bar */}
        <header className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white flex-shrink-0">
          <button
            className="p-2 -ml-2 rounded-lg text-gray-700 hover:bg-gray-100"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
            aria-expanded={mobileOpen}
            aria-controls="operator-sidebar"
          >
            <Menu size={20} />
          </button>
          <span className="text-sm font-display font-700 text-gray-900">Stovalt</span>
          <span className="w-8" aria-hidden="true" />
        </header>

        <main id="main" role="main" tabIndex={-1} className="flex-1 overflow-y-auto p-4 sm:p-6 focus:outline-none">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
